import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { UploadCloud, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FilePreview from "@/components/FilePreview";
import api from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { useLang } from "@/context/LanguageContext";
import { loc } from "@/lib/localize";
import { trackEvent } from "@/lib/analytics";
import { validatePersonName, validateMessageText } from "@/lib/validation";

const MAX_FILES = 10;
const MAX_FILE_SIZE = 15 * 1024 * 1024;
const ACCEPTED = ".pdf,.jpg,.jpeg,.png,.doc,.docx";

function UploadSlot({ slot, file, onSelect, onRemove, t }) {
  const inputId = `slot-${slot.key}`;
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">
        {slot.label} {slot.required ? <span className="text-[var(--gold)]">*</span> : <span className="text-white/35">{t("documents.optional")}</span>}
      </label>
      {file ? (
        <FilePreview file={file} onRemove={onRemove} />
      ) : (
        <>
          <label
            htmlFor={inputId}
            className="flex items-center justify-center gap-2 border border-dashed border-white/20 rounded-xl py-4 cursor-pointer hover:border-[var(--gold)]/50 transition text-white/50 text-sm"
          >
            <UploadCloud size={16} className="text-[var(--gold)]" />
            {t("documents.clickToUpload")} {slot.label.toLowerCase()}
          </label>
          <input
            id={inputId}
            type="file"
            accept={ACCEPTED}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onSelect(f);
              e.target.value = "";
            }}
            data-testid={`documents-slot-${slot.key}`}
          />
        </>
      )}
    </div>
  );
}

export default function DocumentsPage() {
  const { t, lang } = useLang();
  const { content } = useContent();
  const [params] = useSearchParams();
  const services = content?.services || [];

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: params.get("service") || "",
    message: "",
  });
  const [genericFiles, setGenericFiles] = useState([]);
  const [slotFiles, setSlotFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [requirements, setRequirements] = useState({});

  useEffect(() => {
    api.get("/document-requirements").then(({ data }) => {
      setRequirements(data.requirements || {});
    }).catch(() => {
      // non-critical — the form still works, just without labelled slots
    });
  }, []);

  const rawSlots = requirements[form.service_type];
  const slotConfig = rawSlots && rawSlots.length > 0
    ? rawSlots.map((s, idx) => ({ key: String(idx), label: s.label, required: s.required }))
    : null;

  // Switching between a slotted service and the generic area (or between two different
  // slotted services) resets whichever files were collected — a generic file has no slot
  // label, and a previous service's slots don't apply to the new one.
  useEffect(() => {
    setGenericFiles([]);
    setSlotFiles({});
  }, [form.service_type]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  function addGenericFiles(fileList) {
    const incoming = Array.from(fileList);
    const combined = [...genericFiles, ...incoming];
    if (combined.length > MAX_FILES) {
      toast.error(t("documents.maxFiles").replace("{n}", MAX_FILES));
      return;
    }
    const oversized = incoming.find((f) => f.size > MAX_FILE_SIZE);
    if (oversized) {
      toast.error(`"${oversized.name}" ${t("documents.fileTooLarge")}`);
      return;
    }
    setGenericFiles(combined);
  }

  function selectSlotFile(key, file) {
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`"${file.name}" ${t("documents.fileTooLarge")}`);
      return;
    }
    setSlotFiles((s) => ({ ...s, [key]: file }));
  }

  const missingRequiredSlots = slotConfig ? slotConfig.filter((s) => s.required && !slotFiles[s.key]) : [];
  const canSubmit = slotConfig ? missingRequiredSlots.length === 0 : genericFiles.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error(
        slotConfig
          ? `${t("documents.missingRequired")} ${missingRequiredSlots.map((s) => s.label).join(", ")}`
          : t("documents.attachAtLeastOne")
      );
      return;
    }
    const nameError = validatePersonName(form.name);
    if (nameError) {
      toast.error(nameError);
      return;
    }
    const messageError = validateMessageText(form.message);
    if (messageError) {
      toast.error(messageError);
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("service_type", form.service_type);
      if (form.service_type) {
        const svc = services.find((s) => s.id === form.service_type);
        if (svc) fd.append("service_label", loc(svc.title, lang));
      }
      fd.append("message", form.message);

      if (slotConfig) {
        slotConfig.forEach((slot) => {
          const file = slotFiles[slot.key];
          if (file) {
            fd.append("files", file);
            fd.append("labels", slot.label);
          }
        });
      } else {
        genericFiles.forEach((f) => {
          fd.append("files", f);
          fd.append("labels", "");
        });
      }

      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      trackEvent("generate_lead", { method: "documents", service_type: form.service_type || undefined });
      toast.success(t("documents.success"));
      setForm({ name: "", email: "", phone: "", service_type: "", message: "" });
      setGenericFiles([]);
      setSlotFiles({});
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error("You're submitting too quickly — please wait a moment and try again.");
        return;
      }
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("documents.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <main className="bg-[var(--bg)]">
        <section className="pt-28 pb-12 bg-radial-gold border-b border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="overline mb-3">{t("documents.overline")}</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-tight mb-4">
              {t("documents.title")}
            </h1>
            <p className="text-white/65 max-w-2xl mx-auto text-lg">
              {t("documents.subtitle")}
            </p>
          </div>
        </section>

        <section className="py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="card-dark p-6 sm:p-8">
              <form onSubmit={submit} className="space-y-4" data-testid="documents-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("contact.name")}</label>
                    <input required value={form.name} onChange={upd("name")} className="input-dark" data-testid="documents-name" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("contact.email")}</label>
                    <input required type="email" value={form.email} onChange={upd("email")} className="input-dark" data-testid="documents-email" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("contact.phone")}</label>
                    <input
                      type="tel"
                      inputMode="tel"
                      pattern="[0-9+()\-.\s]{6,30}"
                      maxLength={30}
                      value={form.phone}
                      onChange={upd("phone")}
                      className="input-dark"
                      data-testid="documents-phone"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("contact.service")}</label>
                    <select value={form.service_type} onChange={upd("service_type")} className="input-dark" data-testid="documents-service">
                      <option value="">— {t("search.any")} —</option>
                      {services.map((s) => (
                        <option key={s.id} value={s.id}>{loc(s.title, lang)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("documents.notes")}</label>
                  <textarea
                    value={form.message}
                    onChange={upd("message")}
                    rows={4}
                    className="input-dark"
                    data-testid="documents-message"
                  />
                </div>

                {slotConfig ? (
                  <div className="space-y-4" data-testid="documents-slots">
                    <label className="text-xs uppercase tracking-widest text-white/55 block">{t("documents.requiredDocuments")}</label>
                    {slotConfig.map((slot) => (
                      <UploadSlot
                        key={slot.key}
                        slot={slot}
                        file={slotFiles[slot.key]}
                        onSelect={(f) => selectSlotFile(slot.key, f)}
                        onRemove={() => setSlotFiles((s) => ({ ...s, [slot.key]: null }))}
                        t={t}
                      />
                    ))}
                  </div>
                ) : (
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">
                      {t("documents.documentsCount")} ({genericFiles.length}/{MAX_FILES})
                    </label>
                    <label
                      htmlFor="documents-file-input"
                      className="flex flex-col items-center justify-center gap-2 border border-dashed border-white/20 rounded-xl py-8 cursor-pointer hover:border-[var(--gold)]/50 transition text-white/55"
                    >
                      <UploadCloud size={22} className="text-[var(--gold)]" />
                      <span className="text-sm">{t("documents.clickToSelect")}</span>
                      <span className="text-xs text-white/40">{t("documents.acceptedTypes")}</span>
                    </label>
                    <input
                      id="documents-file-input"
                      type="file"
                      multiple
                      accept={ACCEPTED}
                      className="hidden"
                      onChange={(e) => {
                        addGenericFiles(e.target.files);
                        e.target.value = "";
                      }}
                      data-testid="documents-file-input"
                    />
                    {genericFiles.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {genericFiles.map((f, idx) => (
                          <FilePreview
                            key={`${f.name}-${idx}`}
                            file={f}
                            onRemove={() => setGenericFiles((files) => files.filter((_, i) => i !== idx))}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <button type="submit" disabled={submitting || !canSubmit} className="btn-gold w-full justify-center disabled:opacity-40" data-testid="documents-submit">
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
                  {t("documents.submit")}
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
